/**
 * Trip Service
 * API methods for trips, activities, activity types, and ingestion.
 * All URLs are sourced from API_ENDPOINTS to keep the base URL in one place.
 */
import { API_ENDPOINTS, API_BASE_URL } from '../config/api'
import type {
  Trip,
  PaginatedResponse,
  PaginatedTrips,
  Activity,
  PaginatedActivities,
  ActivityType,
  SyncResult,
  StatsResponse,
  SyncStatusResponse,
} from '../types/trip'

// ============================================================================
// TRIP SERVICE
// ============================================================================

export const tripService = {
  async getTrips(page = 1, limit = 50): Promise<PaginatedResponse<Trip>> {
    const res = await fetch(`${API_ENDPOINTS.trips.list}?page=${page}&limit=${limit}`)
    if (!res.ok) throw new Error('Failed to fetch trips')
    return res.json()
  },

  async searchTrips(
    params: Record<string, string>,
    page = 1,
    limit = 50
  ): Promise<PaginatedResponse<Trip>> {
    const query = new URLSearchParams({ ...params, page: String(page), limit: String(limit) })
    const res = await fetch(`${API_ENDPOINTS.trips.search}?${query}`)
    if (!res.ok) throw new Error('Failed to search trips')
    return res.json()
  },

  /**
   * Combined helper used by the trips list page: delegates to searchTrips
   * when a search term is present, otherwise calls getTrips.
   */
  async getTripsOrSearch(
    page: number,
    limit: number,
    search?: string
  ): Promise<PaginatedTrips> {
    if (search && search.trim()) {
      const params: Record<string, string> = /^\d+$/.test(search.trim())
        ? { ritNr: search.trim() }
        : { klantCode: search.trim() }
      return this.searchTrips(params, page, limit)
    }
    return this.getTrips(page, limit)
  },

  async getTrip(ritNr: string): Promise<{ trip: Trip; activities: Activity[] }> {
    const res = await fetch(API_ENDPOINTS.trips.byRitNr(encodeURIComponent(ritNr)))
    if (!res.ok) throw new Error('Failed to fetch trip')
    return res.json()
  },

  async createTrip(data: Partial<Trip> & { ritNr: string }): Promise<Trip> {
    const res = await fetch(API_ENDPOINTS.trips.create, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create trip')
    return res.json()
  },

  async updateTrip(ritNr: string, data: Partial<Trip>): Promise<Trip> {
    const res = await fetch(API_ENDPOINTS.trips.update(encodeURIComponent(ritNr)), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update trip')
    return res.json()
  },

  async deleteTrip(ritNr: string): Promise<void> {
    const res = await fetch(API_ENDPOINTS.trips.delete(encodeURIComponent(ritNr)), {
      method: 'DELETE',
    })
    if (!res.ok) throw new Error('Failed to delete trip')
  },
}

// ============================================================================
// ACTIVITY SERVICE
// ============================================================================

export const activityService = {
  async getActivities(
    page = 1,
    limit = 50,
    ritNr?: string
  ): Promise<PaginatedResponse<Activity>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (ritNr && ritNr.trim()) params.set('ritNr', ritNr.trim())
    const res = await fetch(`${API_ENDPOINTS.activities.list}?${params}`)
    if (!res.ok) throw new Error('Failed to fetch activities')
    return res.json()
  },

  async createActivity(
    data: Partial<Activity> & { id: number; ritNr: string }
  ): Promise<Activity> {
    const res = await fetch(API_ENDPOINTS.activities.create, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create activity')
    return res.json()
  },

  async updateActivity(id: number, data: Partial<Activity>): Promise<Activity> {
    const res = await fetch(API_ENDPOINTS.activities.update(id), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update activity')
    return res.json()
  },

  async deleteActivity(id: number): Promise<void> {
    const res = await fetch(API_ENDPOINTS.activities.delete(id), { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete activity')
  },
}

// ============================================================================
// ACTIVITY TYPE SERVICE
// ============================================================================

export const activityTypeService = {
  async getActivityTypes(): Promise<ActivityType[]> {
    const res = await fetch(API_ENDPOINTS.activityTypes.list)
    if (!res.ok) throw new Error('Failed to fetch activity types')
    const data = await res.json()
    // Backend returns { data: [...] } or plain array
    return Array.isArray(data) ? data : (data.data ?? [])
  },

  /** Alias kept for existing callers */
  getAll(): Promise<ActivityType[]> {
    return this.getActivityTypes()
  },

  async createActivityType(
    data: Omit<ActivityType, 'id'> & { id?: number }
  ): Promise<ActivityType> {
    const res = await fetch(API_ENDPOINTS.activityTypes.create, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create activity type')
    return res.json()
  },

  /** Alias kept for existing callers */
  create(
    payload: { shortCode: string; description: string; groupId?: number; integratorCode?: string }
  ): Promise<ActivityType> {
    return this.createActivityType(payload as Omit<ActivityType, 'id'>)
  },

  async updateActivityType(id: number, data: Partial<ActivityType>): Promise<ActivityType> {
    const res = await fetch(API_ENDPOINTS.activityTypes.update(id), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update activity type')
    return res.json()
  },

  async deleteActivityType(id: number): Promise<void> {
    const res = await fetch(API_ENDPOINTS.activityTypes.delete(id), { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete activity type')
  },

  /** Alias kept for existing callers — returns boolean instead of throwing */
  async delete(id: number): Promise<boolean> {
    try {
      await this.deleteActivityType(id)
      return true
    } catch {
      console.error('Failed to delete activity type')
      return false
    }
  },
}

// ============================================================================
// INGESTION SERVICE
// ============================================================================

export const ingestionService = {
  async triggerSync(): Promise<SyncResult> {
    const res = await fetch(API_ENDPOINTS.ingestion.sync, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to trigger sync')
    return res.json()
  },

  async getStatus(): Promise<SyncStatusResponse> {
    const res = await fetch(API_ENDPOINTS.ingestion.status)
    if (!res.ok) throw new Error('Failed to fetch sync status')
    return res.json()
  },

  /** Alias kept for existing callers */
  getSyncStatus(): Promise<SyncStatusResponse> {
    return this.getStatus()
  },

  async getStats(): Promise<StatsResponse> {
    const res = await fetch(`${API_BASE_URL}/api/stats`)
    if (!res.ok) throw new Error('Failed to fetch stats')
    return res.json()
  },
}